import { useCallback, useEffect } from "react";
import { singleOrderDetailsGenericCallForPostgres } from "../../api/postgreSQL/postCalls/singleOrderDetailsGenericCallForPostgres";
import { AuthStatusChangeComments } from "../../data-model/authStatusChangeComments";
import { SingleOrderDetailsTableName } from "../../enums/singleOrderDetailsTableName";
import { useAbortableFetch } from "../useAbortableFetch";

/** Parameters for the {@link useAuthStatusCommentsRead} hook. */
interface UseAuthStatusCommentsReadProps {
  /** PA order ID to fetch auth-status comments for. */
  orderId: string;
  /** Organization ID scoping the query. */
  orgId: string;
}

/** Response shape returned by the auth-status-comments Postgres endpoint. */
interface AuthStatusCommentsReadResponse {
  comments: AuthStatusChangeComments[];
}

/**
 * Fetches auth-status-change comments for a single medical PA order from Postgres.
 *
 * @param props - The order ID and org ID to fetch auth-status comments for
 * @returns The fetched data, loading state, and any error
 */
export const useAuthStatusCommentsRead = (
  props: UseAuthStatusCommentsReadProps,
) => {
  const { orderId, orgId } = props;

  const fetchAuthStatusComments = useCallback(
    async (signal: AbortSignal) => {
      return singleOrderDetailsGenericCallForPostgres<AuthStatusCommentsReadResponse>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.AUTH_STATUS_COMMENTS,
        },
        signal,
      );
    },
    [orderId, orgId],
  );

  const { runFetch, abort, data, loading, error } = useAbortableFetch(
    fetchAuthStatusComments,
  );

  const skipped = !orderId || !orgId;

  useEffect(() => {
    if (!skipped) {
      runFetch();
    }

    return () => abort();
  }, [skipped, runFetch, abort]);

  return { data, loading, error, skipped };
};
